import { create } from "zustand";

const OFFSET_KEY = "tutti.metroOffset";
export const METRO_OFFSET_MAX = 300;   // ms either way — Bluetooth earbuds can need 150 ms+
const clampOffset = (v: number) => Math.max(-METRO_OFFSET_MAX, Math.min(METRO_OFFSET_MAX, Math.round(v)));
const GRID_KEY = "tutti.metroGrid";
export type MetroGrid = "fixed" | "detected";   // fixed = constant tempo fitted to the song; detected = every detected beat
function readGrid(): MetroGrid {
  try { return localStorage.getItem(GRID_KEY) === "detected" ? "detected" : "fixed"; } catch { return "fixed"; }
}
function readOffset(): number {
  try { const v = Number(localStorage.getItem(OFFSET_KEY)); return Number.isFinite(v) ? clampOffset(v) : 0; } catch { return 0; }
}

export type MetronomeMode = "off" | "overlay" | "solo";   // overlay = clicks over the song; solo = clicks only

interface PlaybackState {
  metronome: MetronomeMode;
  metroOffset: number;      // ms, + = clicks earlier (per-device calibration; persisted)
  metroGrid: MetroGrid;     // click grid (persisted; default fixed tempo)
  isPlaying: boolean;
  currentChord: string;
  currentMeasure: number;   // index of the measure under the cursor (for the chord-grid view); -1 = none
  currentBeatTime: number;  // start time of the beat under the cursor — disambiguates repeated chords in a measure
  volume: number;      // 0..100
  cursorComp: number;  // ms

  setMetronome: (m: MetronomeMode) => void;
  setMetroOffset: (ms: number) => void;
  setMetroGrid: (g: MetroGrid) => void;
  setIsPlaying: (p: boolean) => void;
  setCurrentChord: (c: string) => void;
  setCurrentMeasure: (m: number) => void;
  setCurrentBeatTime: (t: number) => void;
  setVolume: (v: number) => void;
  setCursorComp: (ms: number) => void;
}

export const usePlaybackStore = create<PlaybackState>((set) => ({
  metronome: "off",
  metroOffset: readOffset(),
  metroGrid: readGrid(),
  isPlaying: false,
  currentChord: "—",
  currentMeasure: -1,
  currentBeatTime: -1,
  volume: 100,
  cursorComp: 100,
  setMetronome: (metronome) => set({ metronome }),
  setMetroGrid: (metroGrid) => { set({ metroGrid }); try { localStorage.setItem(GRID_KEY, metroGrid); } catch { /* storage blocked */ } },
  setMetroOffset: (ms) => { const metroOffset = clampOffset(ms); set({ metroOffset }); try { localStorage.setItem(OFFSET_KEY, String(metroOffset)); } catch { /* storage blocked */ } },
  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setCurrentChord: (currentChord) => set({ currentChord }),
  setCurrentMeasure: (currentMeasure) => set({ currentMeasure }),
  setCurrentBeatTime: (currentBeatTime) => set({ currentBeatTime }),
  setVolume: (volume) => set({ volume }),
  setCursorComp: (cursorComp) => set({ cursorComp }),
}));
